import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGameStore } from '../stores/gameStore';
import { useSessionStore } from '../stores/sessionStore';
import { useTransferStore, newTransferId, Transfer } from '../stores/transferStore';
import PlayerStats from '../components/PlayerStats';
import TargetIpLink from '../components/TargetIpLink';
import {
  formatMhz, formatMb, formatMbit,
  SOFTWARE_ROLE, SoftwareType, HackAttackType, ScanResult, HackResult,
  getSoftwareResources, filesHddMb, infectionsHddMb, infectionsRamMb, infectionsCpuMhz,
  FileType, FileEntry, NodeAccessLevel, isIpChangePending,
  PlayerSoftware, RemoteSoftware, RemoteInfection, HdSoftware, HdFile, HackedNode,
  softwareUpgradeCost, softwareUpgradeNextVersion, softwareUpgradeSeconds, encryptedHdUsedMb,
} from '../types';
import { hackApi, softwareApi, fileSystemApi, gameApi } from '../services/api';
import { useNotificationStore } from '../stores/notificationStore';
import { useOnboardingStore } from '../stores/onboardingStore';
import {
  ENTRY_NODE_IP, ACCESS_LOG_FILE, SERVER_LIST_FILE, TUTORIAL_SOFTWARE,
  useOnboardingActiveStep,
} from '../hooks/useOnboarding';
import { sfx } from '../services/sfx';
import { useMediaQuery } from '../hooks/useMediaQuery';

// Single source of truth — the tutorial points players at this same node.
const WHOIS_1_IP = ENTRY_NODE_IP;

type LocalTab = 'files' | 'software' | 'transfers' | 'infections' | 'encryptedhd';
interface Msg { text: string; type: 'success' | 'error' | 'info' }

function apiErrMsg(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } | string; status?: number } };
  // Prefer the server's text: the cooldown length depends on the player's level
  // (early-game acceleration), so it must not be hard-coded here.
  if (typeof e?.response?.data === 'string') return e.response.data;
  const msg = e?.response?.data?.message;
  if (msg) return msg;
  if (e?.response?.status === 429) return 'COOLDOWN ACTIVE — try this target again later';
  return 'Operation failed';
}

function fmtEta(sec: number): string {
  return sec < 60 ? `${sec.toFixed(0)}s` : `${(sec / 60).toFixed(1)}m`;
}

// ── Shared tiny components ────────────────────────────────────────────────────

function FileBadge({ type }: { type: FileType }) {
  const map: Record<FileType, { label: string; color: string }> = {
    [FileType.Text]:            { label: 'TXT', color: '#60a5fa' },
    [FileType.Log]:             { label: 'LOG', color: '#fbbf24' },
    [FileType.SoftwarePackage]: { label: 'PKG', color: '#a78bfa' },
  };
  const { label, color } = map[type] ?? { label: '?', color: '#888' };
  return <span style={{ fontSize: '0.63rem', fontWeight: 'bold', padding: '0.1rem 0.3rem', border: `1px solid ${color}`, color, borderRadius: '2px', minWidth: '2.1rem', textAlign: 'center', fontFamily: 'monospace' }}>{label}</span>;
}

function RoleBadge({ role }: { role: 'ATK' | 'DEF' | 'UTL' }) {
  const colors = { ATK: '#f87171', DEF: '#4ade80', UTL: '#60a5fa' };
  return <span style={{ fontSize: '0.63rem', fontWeight: 'bold', padding: '0.1rem 0.3rem', border: `1px solid ${colors[role]}`, color: colors[role], borderRadius: '2px', minWidth: '2.1rem', textAlign: 'center', fontFamily: 'monospace' }}>{role}</span>;
}

function AccessBadge({ level }: { level: NodeAccessLevel }) {
  const map: Record<NodeAccessLevel, { label: string; color: string }> = {
    [NodeAccessLevel.Full]:    { label: 'FULL', color: '#4ade80' },
    [NodeAccessLevel.FtpOnly]: { label: 'FTP',  color: '#fbbf24' },
    [NodeAccessLevel.SshOnly]: { label: 'SSH',  color: '#60a5fa' },
    [NodeAccessLevel.None]:    { label: 'NONE', color: '#555' },
  };
  const { label, color } = map[level] ?? map[NodeAccessLevel.None];
  return <span style={{ fontSize: '0.63rem', fontWeight: 'bold', padding: '0.1rem 0.35rem', border: `1px solid ${color}`, color, borderRadius: '2px', fontFamily: 'monospace' }}>{label}</span>;
}

function Btn({ label, onClick, disabled, color = 'var(--primary-color)', title, hint }: {
  label: string; onClick: () => void; disabled?: boolean; color?: string; title?: string;
  /** Ring this button (tutorial pointer). Ignored while disabled. */
  hint?: boolean;
}) {
  return (
    <button onClick={onClick} disabled={disabled} title={title} className={hint && !disabled ? 'fx-hint' : undefined} style={{
      // Disabled: muted grey that is still readable against the near-black panels, with a
      // faint fill so the button outline stays visible (the old #2a2a2a/#383838 pair was
      // nearly invisible on this background).
      background: disabled ? '#191d24' : 'transparent',
      border: `1px solid ${disabled ? '#5b6674' : color}`,
      color: disabled ? '#aab4c2' : color,
      padding: '0.15rem 0.5rem',
      borderRadius: '3px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontFamily: 'monospace',
      fontSize: '0.7rem',
      whiteSpace: 'nowrap',
    }}>{label}</button>
  );
}

function MsgBar({ msg }: { msg: Msg | null }) {
  if (!msg) return <div style={{ height: '1.6rem' }} />;
  const color = msg.type === 'success' ? '#4ade80' : msg.type === 'error' ? '#f87171' : '#666';
  return (
    <div style={{ background: '#080808', borderTop: '1px solid #1a1a2e', padding: '0.3rem 0.75rem', fontFamily: 'monospace', fontSize: '0.72rem', color, minHeight: '1.6rem' }}>
      {msg.text}
    </div>
  );
}

// Any IPv4-looking token in a file becomes a tap-to-target link (the looted servers.txt
// is a list of them, and NPC readmes quote their own IP).
const IP_RE = /\b\d{1,3}(?:\.\d{1,3}){3}\b/g;

function lineWithIpLinks(line: string, onPick: (ip: string) => void): React.ReactNode {
  IP_RE.lastIndex = 0;                       // the regex is module-level: reset per line
  const parts: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = IP_RE.exec(line)) !== null) {
    if (m.index > last) parts.push(line.slice(last, m.index));
    parts.push(<TargetIpLink key={m.index} ip={m[0]} onPick={onPick} />);
    last = m.index + m[0].length;
  }
  if (parts.length === 0) return line || ' ';  // keep blank lines visible
  if (last < line.length) parts.push(line.slice(last));
  return parts;
}

function FileReadModal({ name, content, onClose, onPickIp }: {
  name: string; content: string; onClose: () => void; onPickIp: (ip: string) => void;
}) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#0a0a0a', border: '1px solid var(--border-color)', borderRadius: '6px', width: '100%', maxWidth: '700px', maxHeight: '78vh', display: 'flex', flexDirection: 'column', fontFamily: 'monospace' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border-color)', background: '#0d0d0d' }}>
          <span style={{ color: 'var(--primary-color)', fontSize: '0.82rem' }}>{name}</span>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid #333', color: '#888', padding: '0.1rem 0.5rem', borderRadius: '3px', cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.75rem' }}>close</button>
        </div>
        <div style={{ overflow: 'auto', padding: '0.75rem', flex: 1 }}>
          {content.split('\n').map((line, i) => (
            <div key={i} style={{ color: '#ccc', fontSize: '0.8rem', lineHeight: '1.55', whiteSpace: 'pre-wrap' }}>{lineWithIpLinks(line, onPickIp)}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Game() {
  const { player, loadPlayer, infections }             = useGameStore();
  const { session, connect, disconnect, setFiles, setSoftware, setInfections } = useSessionStore();
  const { transfers, addTransfer, removeTransfer }     = useTransferStore();
  const navigate                                       = useNavigate();
  const [searchParams, setSearchParams]                = useSearchParams();

  const [localTab, setLocalTab]         = useState<LocalTab>('software');
  const [targetIpInput, setTargetIpInput] = useState('');
  const [scanResult, setScanResult]     = useState<ScanResult | null>(null);
  const [localMsg, setLocalMsg]         = useState<Msg | null>(null);
  const [remoteMsg, setRemoteMsg]       = useState<Msg | null>(null);
  const [busy, setBusy]                 = useState(false);
  const [readModal, setReadModal]       = useState<{ name: string; content: string } | null>(null);
  const [nowTs, setNowTs]               = useState(() => Date.now());
  const [hackedNodes, setHackedNodes]   = useState<HackedNode[]>([]);
  // Attack feedback flash on the remote panel ('success' green pulse / 'fail' red shake).
  const [attackFx, setAttackFx]         = useState<'success' | 'fail' | null>(null);

  // Narrow viewports (phones, small portal iframes) show ONE panel at a time behind a
  // Local/Remote switch: the two stacked panels don't fit without heavy scrolling.
  // Both stay mounted (hidden with display:none) so switching never resets their state.
  const narrow = useMediaQuery('(max-width: 900px)');
  // Defaults to 'local' to match the desktop stacking order (own system on top).
  const [mobileTab, setMobileTab] = useState<'local' | 'remote'>('local');

  // Clear the flash once its animation has played, so the next attack re-triggers it.
  useEffect(() => {
    if (!attackFx) return;
    const id = setTimeout(() => setAttackFx(null), 700);
    return () => clearTimeout(id);
  }, [attackFx]);

  // Hacked nodes live in a dedicated container now — fetch them for the connect UI.
  const loadHackedNodes = useCallback(async () => {
    try {
      const res = await gameApi.getHackedNodes();
      setHackedNodes((res.data ?? []) as HackedNode[]);
    } catch { /* non-critical */ }
  }, []);

  // 1s clock so in-progress infection ETAs count down.
  useEffect(() => { const id = setInterval(() => setNowTs(Date.now()), 1000); return () => clearInterval(id); }, []);

  // Load hacked nodes on mount (direct load / F5). Own-node infections come from the
  // game store (loadPlayer fetches them alongside the player).
  useEffect(() => { loadHackedNodes(); }, [loadHackedNodes]);

  // Tapping an IP elsewhere (Targets / Hacked Nodes) routes here as ?target=<ip> to
  // prefill the field — far easier than typing an IP on a phone. Ignored while a session
  // is open (the target can only be set from a disconnected state) and the param is
  // stripped either way so a later refresh doesn't re-apply a stale IP.
  useEffect(() => {
    const wanted = searchParams.get('target');
    if (!wanted) return;
    if (!session) {
      setTargetIpInput(wanted);
      setScanResult(null);
      setMobileTab('remote');   // narrow screens: show the panel holding the field
    }
    const next = new URLSearchParams(searchParams);
    next.delete('target');
    setSearchParams(next, { replace: true });
  }, [searchParams, session, setSearchParams]);

  // Every rejection/failure funnels through these, so the "denied" cue lives here once
  // instead of at ~20 call sites. Positive cues are played explicitly by the handlers
  // that deserve one (scan/connect/attack/…), so nothing ever double-plays.
  const lMsg = useCallback((text: string, type: Msg['type'] = 'info') => {
    if (type === 'error') sfx.fail();
    setLocalMsg({ text, type });
  }, []);
  const rMsg = useCallback((text: string, type: Msg['type'] = 'info') => {
    if (type === 'error') sfx.fail();
    setRemoteMsg({ text, type });
  }, []);

  const doLocal = useCallback(async (fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); } catch (err) { lMsg(apiErrMsg(err), 'error'); } finally { setBusy(false); }
  }, [lMsg]);

  const doRemote = useCallback(async (fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); } catch (err) { rMsg(apiErrMsg(err), 'error'); } finally { setBusy(false); }
  }, [rMsg]);

  // ── Software ──────────────────────────────────────────────────────────────

  const handleRun = useCallback((sw: PlayerSoftware) => doLocal(async () => {
    sfx.click();
    await softwareApi.run(sw.id);
    await loadPlayer();   // also refreshes own-node infections (AntiVirus may have cured some)
    const res = getSoftwareResources(sw.type, sw.version);
    lMsg(`${sw.type} v${sw.version.toFixed(1)} running — RAM:${formatMb(res.ramMb)} CPU:${formatMhz(res.cpuMhz)}`, 'success');
    useNotificationStore.getState().push({
      id: `sw-run-${sw.id}-${Date.now()}`, type: 'software_run', title: 'SOFTWARE STARTED',
      message: `${sw.type} v${sw.version.toFixed(1)} is now running.`, createdAt: new Date().toISOString(),
    });
  }), [doLocal, loadPlayer, lMsg]);

  const handleStop = useCallback((sw: PlayerSoftware) => doLocal(async () => {
    sfx.remove();
    await softwareApi.stop(sw.id);
    await loadPlayer();
    lMsg(`${sw.type} v${sw.version.toFixed(1)} stopped.`, 'info');
    useNotificationStore.getState().push({
      id: `sw-stop-${sw.id}-${Date.now()}`, type: 'software_stop', title: 'SOFTWARE STOPPED',
      message: `${sw.type} v${sw.version.toFixed(1)} terminated.`, createdAt: new Date().toISOString(),
    });
  }), [doLocal, loadPlayer, lMsg]);

  const handleUpgrade = useCallback((sw: PlayerSoftware) => doLocal(async () => {
    const next = softwareUpgradeNextVersion(sw.version);
    const secs = softwareUpgradeSeconds(sw.version);
    await softwareApi.upgrade(sw.id);
    await loadPlayer();
    lMsg(`Upgrading ${sw.type} → v${next.toFixed(1)} (~${secs}s — fixed time, independent of your internet line).`, 'success');
    useNotificationStore.getState().push({
      id: `sw-upg-${sw.id}-${Date.now()}`, type: 'software_upgrade', title: 'UPGRADE STARTED',
      message: `${sw.type} is upgrading to v${next.toFixed(1)} (~${secs}s, fixed — the internet line speed does not affect upgrades).`, createdAt: new Date().toISOString(),
    });
  }), [doLocal, loadPlayer, lMsg]);

  const handleBackupSoftware = useCallback((sw: PlayerSoftware) => doLocal(async () => {
    await gameApi.hdBackup('software', sw.id);
    await loadPlayer();
    lMsg(`Backing up ${sw.type} v${sw.version.toFixed(1)} to the encrypted HD.`, 'success');
  }), [doLocal, loadPlayer, lMsg]);

  const handleBackupFile = useCallback((f: FileEntry) => doLocal(async () => {
    await gameApi.hdBackup('file', f.id);
    await loadPlayer();
    lMsg(`Backing up ${f.name} to the encrypted HD.`, 'success');
  }), [doLocal, loadPlayer, lMsg]);

  const handleRestoreSoftware = useCallback((s: HdSoftware) => doLocal(async () => {
    await gameApi.hdRestore('software', s.type);
    await loadPlayer();
    lMsg(`Restoring ${s.type} v${s.version.toFixed(1)} from the encrypted HD.`, 'success');
  }), [doLocal, loadPlayer, lMsg]);

  const handleRestoreFile = useCallback((f: HdFile) => doLocal(async () => {
    await gameApi.hdRestore('file', f.name);
    await loadPlayer();
    lMsg(`Restoring ${f.name} from the encrypted HD.`, 'success');
  }), [doLocal, loadPlayer, lMsg]);

  const handleCancelDownload = useCallback((sw: PlayerSoftware) => doLocal(async () => {
    const tr = transfers.find(t => t.softwareId === sw.id);
    if (tr) removeTransfer(tr.id);
    await softwareApi.cancel(sw.id);
    await loadPlayer();
    lMsg(`${sw.type} download cancelled — ${sw.purchasePrice.toLocaleString()} cr refunded.`, 'success');
  }), [doLocal, transfers, removeTransfer, loadPlayer, lMsg]);

  // ── Local files ───────────────────────────────────────────────────────────

  const handleLocalRead = useCallback((f: FileEntry) => doLocal(async () => {
    const resp = await fileSystemApi.localRead(f.id);
    setReadModal({ name: f.name, content: (resp.data as { content: string }).content });
  }), [doLocal]);

  const handleLocalDelete = useCallback((f: FileEntry) => doLocal(async () => {
    await fileSystemApi.deleteFile(f.id);
    await loadPlayer();
    lMsg(`${f.name} deleted.`, 'info');
  }), [doLocal, loadPlayer, lMsg]);

  // ── Remote ────────────────────────────────────────────────────────────────

  const handleScan = useCallback(() => doRemote(async () => {
    const ip = targetIpInput.trim();
    if (!ip) { rMsg('Enter a target IP address', 'error'); return; }
    setScanResult(null);
    sfx.scan();
    const resp = await hackApi.scan(ip);
    setScanResult(resp.data as ScanResult);
    rMsg(`Scan complete — ${ip}`, 'success');
  }), [doRemote, targetIpInput, rMsg]);

  const handleHack = useCallback((type: HackAttackType) => doRemote(async () => {
    const ip = targetIpInput.trim();
    if (!ip) { rMsg('Enter a target IP address', 'error'); return; }
    if (player && isIpChangePending(player.ipChangePendingAt)) { rMsg('DENIED — IP change in progress', 'error'); return; }
    sfx.attack();
    setAttackFx(null);
    const resp = await hackApi.attack(ip, type);
    const r = resp.data as HackResult;
    if (r.success) {
      sfx.success();
      setAttackFx('success');
      // Advances the tutorial (a non-entry node also satisfies the "next target" step).
      useOnboardingStore.getState().notifyHackSuccess(ip === ENTRY_NODE_IP);

      let msg = `ACCESS GRANTED — ${r.message}`;
      if (r.creditsStolen > 0)    msg += ` | +${r.creditsStolen.toLocaleString()} cr`;
      if (r.experienceGained > 0) msg += ` | +${r.experienceGained} XP`;
      rMsg(msg, 'success');
    } else {
      setAttackFx('fail');
      rMsg(`INTRUSION DETECTED — ${r.message}`, 'error');
    }
    await loadPlayer();
    await loadHackedNodes();
  }), [doRemote, targetIpInput, player, loadPlayer, loadHackedNodes, rMsg]);

  const handleConnect = useCallback(() => doRemote(async () => {
    const ip = targetIpInput.trim();
    if (!ip) { rMsg('Enter a target IP address', 'error'); return; }
    if (player && isIpChangePending(player.ipChangePendingAt)) { rMsg('DENIED — IP change in progress', 'error'); return; }
    const resp = await fileSystemApi.connect(ip);
    const d = resp.data as {
      username: string; level: number; internetSpeedMBit: number; files: FileEntry[]; accessLevel: string;
      software?: RemoteSoftware[]; softwareHidden?: boolean; infections?: RemoteInfection[];
      cpuMhz?: number; ramMb?: number; hddMb?: number;
    };
    const accessLevel = (d.accessLevel ?? NodeAccessLevel.Full) as NodeAccessLevel;
    connect({
      targetIp: ip, targetUsername: d.username, targetInternetMbit: d.internetSpeedMBit,
      files: d.files, accessLevel,
      software: d.software ?? [], softwareHidden: d.softwareHidden ?? false,
      infections: d.infections ?? [],
      cpuMhz: d.cpuMhz ?? 0, ramMb: d.ramMb ?? 0, hddMb: d.hddMb ?? 0,
    });
    sfx.connect();
    setMobileTab('remote'); // on phones, jump straight to the node we just opened
    rMsg(`Connected to ${d.username} (${ip}) — ${d.files.length} files`, 'success');
  }), [doRemote, targetIpInput, player, connect, rMsg]);

  const handleDisconnect = useCallback(() => {
    const ip = session?.targetIp;
    disconnect();
    rMsg(`Disconnected from ${ip}.`, 'info');
  }, [session, disconnect, rMsg]);

  const handleRefreshFiles = useCallback(() => doRemote(async () => {
    if (!session) return;
    const [filesResp, swResp] = await Promise.all([
      fileSystemApi.listFiles(session.targetIp),
      fileSystemApi.listSoftware(session.targetIp),
    ]);
    const files = filesResp.data as FileEntry[];
    const sw = swResp.data as { software: RemoteSoftware[]; softwareHidden: boolean; infections?: RemoteInfection[] };
    setFiles(files);
    setSoftware(sw.software, sw.softwareHidden);
    if (sw.infections) setInfections(sw.infections);
    rMsg(`Refreshed — ${files.length} files`, 'info');
  }), [doRemote, session, setFiles, setSoftware, setInfections, rMsg]);

  const handleRemoteStopSoftware = useCallback((s: RemoteSoftware) => doRemote(async () => {
    if (!session) return;
    const resp = await fileSystemApi.stopSoftware(session.targetIp, s.id);
    const d = resp.data as { software: RemoteSoftware[]; softwareHidden: boolean };
    setSoftware(d.software, d.softwareHidden);
    rMsg(`${s.type} stopped on ${session.targetIp}.`, 'info');
  }), [doRemote, session, setSoftware, rMsg]);

  const handleRemoteDeleteSoftware = useCallback((s: RemoteSoftware) => doRemote(async () => {
    if (!session) return;
    const resp = await fileSystemApi.deleteSoftware(session.targetIp, s.id);
    const d = resp.data as { software: RemoteSoftware[]; softwareHidden: boolean };
    setSoftware(d.software, d.softwareHidden);
    rMsg(`${s.type} deleted from ${session.targetIp}.`, 'info');
  }), [doRemote, session, setSoftware, rMsg]);

  const handleInfect = useCallback(() => doRemote(async () => {
    if (!session) return;
    sfx.click();
    const resp = await fileSystemApi.infect(session.targetIp);
    const d = resp.data as { infectedVersion: number; uploadSeconds: number; infections?: RemoteInfection[] };
    // Show the new infection (with its live upload progress) immediately.
    if (d.infections) setInfections(d.infections);
    await loadPlayer();
    await loadHackedNodes();
    rMsg(`Infecting ${session.targetUsername} with Trojan v${d.infectedVersion.toFixed(1)} — upload ~${fmtEta(d.uploadSeconds)}.`, 'success');
  }), [doRemote, session, loadPlayer, loadHackedNodes, setInfections, rMsg]);

  const handleRemoteDownloadSoftware = useCallback((s: RemoteSoftware) => doRemote(async () => {
    if (!session) return;
    sfx.click();
    const resp = await fileSystemApi.downloadSoftware(session.targetIp, s.id);
    const d = resp.data as { softwareId: string; fileSizeMb: number; effectiveSpeedMbit: number };
    addTransfer({
      id: newTransferId(), kind: 'software', name: s.type, softwareId: d.softwareId,
      softwareType: s.type, version: s.version, fileSizeMb: d.fileSizeMb,
      downloadedMb: 0, effectiveSpeedMbit: d.effectiveSpeedMbit,
    });
    await loadPlayer();
    const eta = fmtEta(d.fileSizeMb * 8 / Math.max(d.effectiveSpeedMbit, 0.001));
    rMsg(`Downloading ${s.type} v${s.version.toFixed(1)} — ETA ~${eta}`, 'success');
  }), [doRemote, session, addTransfer, loadPlayer, rMsg]);

  const handleRemoteRead = useCallback((f: FileEntry) => doRemote(async () => {
    if (!session) return;
    const resp = await fileSystemApi.readFile(session.targetIp, f.id);
    setReadModal({ name: `${f.name} [${session.targetIp}]`, content: (resp.data as { content: string }).content });
  }), [doRemote, session]);

  const handleRemoteDelete = useCallback((f: FileEntry) => doRemote(async () => {
    if (!session) return;
    sfx.remove();
    await fileSystemApi.deleteFile(f.id, session.targetIp);
    setFiles(session.files.filter(x => x.id !== f.id));
    // Wiping the access log is a tutorial step ("cover your tracks").
    if (f.name === ACCESS_LOG_FILE) useOnboardingStore.getState().notifyLogDeleted();
    rMsg(`${f.name} deleted${f.type === FileType.Log ? ' [untraced]' : ''}.`, 'info');
  }), [doRemote, session, setFiles, rMsg]);

  const handleRemoteDownload = useCallback((f: FileEntry) => doRemote(async () => {
    if (!session) return;
    const resp = await fileSystemApi.download(session.targetIp, f.id);
    const d = resp.data as {
      transferType: string; pendingId?: string;
      softwareId?: string; softwareType?: string; softwareVersion?: number;
      fileEntry?: FileEntry; content?: string;
      fileSizeMb: number; effectiveSpeedMbit: number;
    };
    const eta = fmtEta(d.fileSizeMb * 8 / Math.max(d.effectiveSpeedMbit, 0.001));
    if (d.transferType === 'software' && d.softwareId) {
      addTransfer({ id: newTransferId(), kind: 'software', name: f.name, softwareId: d.softwareId, version: d.softwareVersion, fileSizeMb: d.fileSizeMb, downloadedMb: 0, effectiveSpeedMbit: d.effectiveSpeedMbit });
      await loadPlayer();
      rMsg(`Software queued — ETA ~${eta}`, 'success');
    } else if (d.transferType === 'file' && d.fileEntry) {
      addTransfer({ id: d.pendingId ?? newTransferId(), kind: 'file', name: f.name, fileName: d.fileEntry.name, fileContent: d.content ?? '', fileSizeMb: d.fileSizeMb, downloadedMb: 0, effectiveSpeedMbit: d.effectiveSpeedMbit });
      rMsg(`Downloading ${f.name} — ETA ~${eta}`, 'success');
    }
  }), [doRemote, session, addTransfer, loadPlayer, rMsg]);

  const handleCancelTransfer = useCallback((t: Transfer) => doLocal(async () => {
    if (t.kind === 'software' && t.softwareId) {
      // Reverts/refunds server-side (DeleteSoftware handles download vs upgrade).
      await softwareApi.cancel(t.softwareId);
      removeTransfer(t.id);
      await loadPlayer();
      lMsg(`${t.name} ${t.isUpgrade ? 'upgrade' : 'download'} cancelled.`, 'info');
    } else {
      // File: drop the client transfer and the persisted pending record so it
      // doesn't resume on the next load.
      removeTransfer(t.id);
      await fileSystemApi.cancelDownload(t.id).catch(() => { /* best effort */ });
      lMsg(`${t.name} download cancelled.`, 'info');
    }
  }), [doLocal, removeTransfer, loadPlayer, lMsg]);

  // ── Derived state ─────────────────────────────────────────────────────────

  const ipChangePending = player ? isIpChangePending(player.ipChangePendingAt) : false;
  const sw = player?.software ?? [];
  const isRun = (t: SoftwareType) => sw.some(s => s.type === t && s.isRunning);
  const hasPortScanner = isRun(SoftwareType.PortScanner);
  const hasCracker     = isRun(SoftwareType.Cracker);
  const canExploit     = isRun(SoftwareType.FtpExploit) || isRun(SoftwareType.SshExploit);
  const hasDdos        = isRun(SoftwareType.Ddos);
  const hasTrojan      = isRun(SoftwareType.Trojan);
  const knownNode      = hackedNodes.find(n => n.ip === targetIpInput.trim());
  const noIp           = !targetIpInput.trim();

  // ── Tutorial pointers ─────────────────────────────────────────────────────
  // Ring the exact control the current step needs. Every flag is false once the
  // tutorial is skipped/finished, because useOnboardingActiveStep() returns null then.
  const tutStep     = useOnboardingActiveStep();
  const tutLogWiped = useOnboardingStore(s => s.logDeleted);
  const remoteHasLog = session?.files.some(f => f.name === ACCESS_LOG_FILE) ?? false;
  const tut = {
    // Which panel to look at on narrow screens (step 5 needs the list first, then the attack).
    mobileLocal:  tutStep === 1 || (tutStep === 5 && noIp),
    mobileRemote: tutStep === 2 || tutStep === 3 || tutStep === 4 || (tutStep === 5 && !noIp),
    softwareTab:  tutStep === 1 && localTab !== 'software',
    filesTab:     tutStep === 5 && localTab !== 'files',
    runTool:      tutStep === 1,                       // RUN on the tutorial tool
    readList:     tutStep === 5,                       // Read servers.txt to find targets
    ipInput:      (tutStep === 2 || tutStep === 3) && noIp,
    connect:      tutStep === 3 && !session,
    bruteForce:   tutStep === 2 || tutStep === 5,
    downloadList: tutStep === 3 && !!session,          // Download servers.txt
    deleteLog:    tutStep === 4 && !!session && remoteHasLog && !tutLogWiped,
    refresh:      tutStep === 4 && !!session && !remoteHasLog && !tutLogWiped,
    disconnect:   tutStep === 4 && !!session && tutLogWiped,
  };

  // ── Styles ────────────────────────────────────────────────────────────────

  const panelS: React.CSSProperties = {
    background: '#0a0a0a', border: '1px solid var(--border-color)', borderRadius: '6px',
    display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: 'monospace',
    minHeight: '260px',
  };

  const headerS: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0.5rem 0.75rem', background: '#0d0d0d',
    borderBottom: '1px solid var(--border-color)', gap: '0.5rem', flexWrap: 'wrap',
  };

  const tabBtn = (active: boolean): React.CSSProperties => ({
    background: active ? 'var(--primary-color)' : 'transparent',
    border: `1px solid ${active ? 'var(--primary-color)' : '#2a2a2a'}`,
    color: active ? '#000' : '#666',
    padding: '0.18rem 0.6rem', borderRadius: '3px', cursor: 'pointer',
    fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: active ? 'bold' : 'normal',
  });

  const rowS: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    padding: '0.4rem 0.75rem', borderBottom: '1px solid #111', flexWrap: 'wrap',
  };

  const emptyS: React.CSSProperties = {
    padding: '1.25rem', color: '#6b7280', fontSize: '0.78rem', textAlign: 'center',
  };

  const sectionHdrS: React.CSSProperties = {
    padding: '0.3rem 0.75rem', color: '#9ca3af', fontSize: '0.62rem',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    background: '#0d0d0d', borderBottom: '1px solid #111', borderTop: '1px solid #111',
  };

  return (
    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>

      {/* ── Dual panels ── */}
      {/* High grow (999) so beside the sidebar this panel eats nearly all the extra
          width, keeping the sidebar at ~300px on desktop; when the row wraps the sidebar
          (grow:1, alone on its line) expands to full width instead. */}
      <div style={{ flex: '999 1 440px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1rem' }}>

        {/* Local/Remote switch — narrow viewports only (one panel fits at a time). */}
        {narrow && (
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {([
              { key: 'local'  as const, label: 'LOCAL' },
              { key: 'remote' as const, label: session ? `REMOTE · ${session.targetIp}` : 'REMOTE' },
            ]).map(t => (
              <button
                key={t.key}
                onClick={() => { setMobileTab(t.key); sfx.click(); }}
                className={
                  mobileTab !== t.key &&
                  ((t.key === 'local' && tut.mobileLocal) || (t.key === 'remote' && tut.mobileRemote))
                    ? 'fx-hint' : undefined
                }
                style={{
                  flex: 1, padding: '0.45rem 0.5rem', borderRadius: '4px', cursor: 'pointer',
                  fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 'bold',
                  background: mobileTab === t.key ? 'var(--primary-color)' : 'transparent',
                  border: `1px solid ${mobileTab === t.key ? 'var(--primary-color)' : '#2a2a2a'}`,
                  color: mobileTab === t.key ? '#000' : '#888',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* ──────────────────── LOCAL PANEL ──────────────────────────────────── */}
        <div style={{ ...panelS, ...(narrow && mobileTab !== 'local' ? { display: 'none' } : null) }}>
          <div style={headerS}>
            <span style={{ color: 'var(--primary-color)', fontSize: '0.78rem', fontWeight: 'bold' }}>
              Local System{player ? `  —  ${player.ipAddress || ''}` : ''}
            </span>
            <div style={{ display: 'flex', gap: '0.3rem' }}>
              {(['software', 'files', 'transfers', 'infections', 'encryptedhd'] as LocalTab[]).map(t => (
                <button
                  key={t}
                  style={tabBtn(localTab === t)}
                  onClick={() => setLocalTab(t)}
                  className={
                    (t === 'software' && tut.softwareTab) || (t === 'files' && tut.filesTab)
                      ? 'fx-hint' : undefined
                  }
                >
                  {t === 'transfers'
                    ? `Transfers${transfers.length > 0 ? ` (${transfers.length})` : ''}`
                    : t === 'infections'
                      ? `Infections${infections.length > 0 ? ` (${infections.length})` : ''}`
                      : t === 'encryptedhd'
                        ? 'Encrypted HD'
                        : t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div style={{ flex: 1, overflow: 'auto' }}>

            {/* ── Software tab ── */}
            {localTab === 'software' && (sw.length > 0 ? sw.map(item => {
              const role = SOFTWARE_ROLE[item.type];
              const res  = getSoftwareResources(item.type, item.version);
              const tr   = transfers.find(t => t.softwareId === item.id);
              const pct  = tr ? Math.min(100, (tr.downloadedMb / tr.fileSizeMb) * 100) : 0;
              // Backup only adds value when the encrypted HD has no copy of this
              // software, or holds a strictly lower version (mirrors the backend).
              const hdCopy   = player?.encryptedHd?.software.find(b => b.type === item.type);
              const canBackup = !!player?.encryptedHd && item.isDownloaded && (!hdCopy || hdCopy.version < item.version);
              return (
                <div key={item.id} style={rowS}>
                  <RoleBadge role={role} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <span style={{ color: '#e2e8f0', fontSize: '0.8rem' }}>{item.type}</span>
                      <span style={{ color: '#9ca3af', fontSize: '0.68rem' }}>v{item.version.toFixed(1)}</span>
                      {item.isRunning   && <span style={{ color: '#4ade80', fontSize: '0.62rem' }}>● RUN</span>}
                      {!item.isDownloaded && (item.upgradeFromVersion != null
                        ? <span style={{ color: '#fbbf24', fontSize: '0.62rem' }}>↑ UPG</span>
                        : <span style={{ color: '#fbbf24', fontSize: '0.62rem' }}>↓ DL</span>)}
                    </div>
                    <div style={{ color: '#9ca3af', fontSize: '0.65rem', marginTop: '0.1rem' }}>
                      HDD:{formatMb(res.hddMb)} · RAM:{formatMb(res.ramMb)} · CPU:{formatMhz(res.cpuMhz)}
                    </div>
                    {tr && (
                      <div style={{ marginTop: '0.25rem' }}>
                        <div style={{ height: '3px', background: '#1a1a2e', borderRadius: '2px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: '#fbbf24' }} />
                        </div>
                        <span style={{ color: '#555', fontSize: '0.6rem' }}>{pct.toFixed(0)}%</span>
                      </div>
                    )}
                  </div>
                  {item.isDownloaded && !item.isRunning && <Btn label="Run"    color="#4ade80" onClick={() => handleRun(item)}            disabled={busy} hint={tut.runTool && item.type === TUTORIAL_SOFTWARE} />}
                  {item.isDownloaded && !item.isRunning && (
                    <Btn
                      label={`↑ v${softwareUpgradeNextVersion(item.version).toFixed(1)} · ${softwareUpgradeCost(item.type, item.version).toLocaleString()}cr`}
                      color="#60a5fa"
                      onClick={() => handleUpgrade(item)}
                      disabled={busy || (player ? player.credits < softwareUpgradeCost(item.type, item.version) : true)}
                    />
                  )}
                  {item.isRunning                        && <Btn label="Stop"   color="#f87171" onClick={() => handleStop(item)}           disabled={busy} />}
                  {canBackup && <Btn label="Backup" color="#a78bfa" onClick={() => handleBackupSoftware(item)} disabled={busy} />}
                  {!item.isDownloaded                    && <Btn label="Cancel" color="#fbbf24" onClick={() => handleCancelDownload(item)} disabled={busy} />}
                </div>
              );
            }) : (
              <div style={emptyS}>
                No software installed.{' '}
                <button onClick={() => navigate('/store')} style={{ background: 'transparent', border: 'none', color: 'var(--primary-color)', cursor: 'pointer', fontFamily: 'monospace', textDecoration: 'underline', fontSize: '0.78rem' }}>
                  Visit the store
                </button>
              </div>
            ))}

            {/* ── Files tab ── */}
            {localTab === 'files' && (player?.files && player.files.length > 0 ? player.files.map(f => (
              <div key={f.id} style={rowS}>
                <FileBadge type={f.type} />
                <span style={{ flex: 1, color: '#e2e8f0', fontSize: '0.8rem', minWidth: 0, overflowWrap: 'anywhere' }}>{f.name}</span>
                <span style={{ color: '#444', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{f.sizeMb.toFixed(2)} MB</span>
                {(f.type === FileType.Text || f.type === FileType.Log) && (
                  <Btn label="Read" onClick={() => handleLocalRead(f)} disabled={busy} hint={tut.readList && f.name === SERVER_LIST_FILE} />
                )}
                {player?.encryptedHd && f.type === FileType.Text && (
                  <Btn label="Backup" color="#a78bfa" onClick={() => handleBackupFile(f)} disabled={busy} />
                )}
                {f.isDeletable && (
                  <Btn label="Delete" color="#f87171" onClick={() => handleLocalDelete(f)} disabled={busy} />
                )}
              </div>
            )) : (
              <div style={emptyS}>No local files. Download files from remote systems.</div>
            ))}

            {/* ── Transfers tab ── */}
            {localTab === 'transfers' && (transfers.length > 0 ? transfers.map(t => {
              const pct    = Math.min(100, (t.downloadedMb / t.fileSizeMb) * 100);
              const etaSec = t.effectiveSpeedMbit
                ? (t.fileSizeMb - t.downloadedMb) * 8 / Math.max(t.effectiveSpeedMbit, 0.001)
                : 0;
              return (
                <div key={t.id} style={rowS}>
                  <span style={{ fontSize: '0.62rem', color: t.isUpgrade ? '#fbbf24' : t.kind === 'software' ? '#a78bfa' : '#60a5fa', border: '1px solid currentColor', padding: '0.1rem 0.3rem', borderRadius: '2px', minWidth: '2.1rem', textAlign: 'center' }}>
                    {t.isUpgrade ? 'UPG' : t.kind === 'software' ? 'SW' : 'FILE'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: '#e2e8f0', fontSize: '0.78rem', overflowWrap: 'anywhere' }}>
                      {t.name}{t.isUpgrade && t.version != null ? ` → v${t.version.toFixed(1)}` : ''}
                    </div>
                    {t.isUpgrade && (
                      <div style={{ color: '#fbbf24', fontSize: '0.58rem', marginTop: '0.05rem' }}>
                        upgrade — does not use your internet line speed
                      </div>
                    )}
                    <div style={{ marginTop: '0.2rem' }}>
                      {/* Live sheen while the transfer is still running, so it reads as active. */}
                      <div style={{ height: '4px', background: '#1a1a2e', borderRadius: '2px', overflow: 'hidden' }}>
                        <div
                          className={pct < 100 ? 'fx-progress-live' : undefined}
                          style={{ height: '100%', width: `${pct}%`, background: 'var(--primary-color)', transition: 'width 0.5s ease' }}
                        />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.1rem' }}>
                        <span style={{ color: '#9ca3af', fontSize: '0.6rem' }}>{pct.toFixed(0)}% · {t.downloadedMb.toFixed(2)}/{t.fileSizeMb.toFixed(2)} MB</span>
                        {etaSec > 0 && <span style={{ color: '#9ca3af', fontSize: '0.6rem' }}>~{fmtEta(etaSec)}</span>}
                      </div>
                    </div>
                  </div>
                  <Btn label="Cancel" color="#f87171" onClick={() => handleCancelTransfer(t)} disabled={busy} />
                </div>
              );
            }) : (
              <div style={emptyS}>No active transfers.</div>
            ))}

            {/* ── Infections tab (view-only: infections running on YOUR node) ── */}
            {localTab === 'infections' && (infections.length > 0 ? infections.map(inf => {
              const res = getSoftwareResources(SoftwareType.Trojan, inf.virusVersion);
              const start = new Date(inf.infectedAt).getTime();
              const end   = new Date(inf.activeAt).getTime();
              const remainSec = Math.ceil((end - nowTs) / 1000);
              const uploading = remainSec > 0;
              const pct = uploading && end > start
                ? Math.min(100, Math.max(0, ((nowTs - start) / (end - start)) * 100))
                : 100;
              return (
                <div key={inf.id} style={rowS}>
                  <RoleBadge role="ATK" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <span style={{ color: '#e2e8f0', fontSize: '0.8rem' }}>Trojan</span>
                      <span style={{ color: '#9ca3af', fontSize: '0.68rem' }}>v{inf.virusVersion.toFixed(1)}</span>
                      {uploading
                        ? <span style={{ color: '#fbbf24', fontSize: '0.62rem' }}>↑ infecting… ~{fmtEta(remainSec)}</span>
                        : <span style={{ color: '#f87171', fontSize: '0.62rem' }}>● active</span>}
                    </div>
                    <div style={{ color: '#9ca3af', fontSize: '0.65rem', marginTop: '0.1rem' }}>
                      HDD:{formatMb(res.hddMb)} · RAM:{formatMb(res.ramMb)} · CPU:{formatMhz(res.cpuMhz)}
                    </div>
                    {uploading && (
                      <div style={{ marginTop: '0.25rem' }}>
                        <div style={{ height: '3px', background: '#1a1a2e', borderRadius: '2px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: '#fbbf24', transition: 'width 0.5s ease' }} />
                        </div>
                        <span style={{ color: '#555', fontSize: '0.6rem' }}>{pct.toFixed(0)}%</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            }) : (
              <div style={emptyS}>No active infections on your node.</div>
            ))}

            {/* ── Encrypted HD tab (view-only backups + restore download) ── */}
            {localTab === 'encryptedhd' && (!player?.encryptedHd ? (
              <div style={emptyS}>No encrypted HD.</div>
            ) : (() => {
              const hd   = player.encryptedHd;
              const used = encryptedHdUsedMb(hd);
              const cap  = hd.capacityMb;
              const pct  = cap > 0 ? Math.min(100, (used / cap) * 100) : 0;
              const isEmpty = hd.software.length === 0 && hd.files.length === 0;
              return (
                <>
                  <div style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid #111' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'monospace', fontSize: '0.7rem', color: '#9ca3af', marginBottom: '0.25rem' }}>
                      <span>Backup space</span>
                      <span>{formatMb(used)} / {formatMb(cap)}</span>
                    </div>
                    <div style={{ height: '4px', background: '#1a1a2e', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: '#a78bfa' }} />
                    </div>
                  </div>
                  {isEmpty ? (
                    <div style={emptyS}>
                      Encrypted HD empty. {cap === 0 ? 'Buy capacity in the store, then ' : ''}use Backup on your software or files to store safe copies here.
                    </div>
                  ) : (
                    <>
                      {hd.software.map(s => {
                        const res       = getSoftwareResources(s.type, s.version);
                        const remainSec = Math.ceil((new Date(s.readyAt).getTime() - nowTs) / 1000);
                        const ready     = remainSec <= 0;
                        const local     = sw.find(x => x.type === s.type);
                        const canRestore = ready && (!local || (!local.isRunning && local.version < s.version));
                        return (
                          <div key={`sw-${s.type}`} style={rowS}>
                            <RoleBadge role={SOFTWARE_ROLE[s.type]} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                                <span style={{ color: '#e2e8f0', fontSize: '0.8rem' }}>{s.type}</span>
                                <span style={{ color: '#9ca3af', fontSize: '0.68rem' }}>v{s.version.toFixed(1)}</span>
                                {!ready && <span style={{ color: '#fbbf24', fontSize: '0.62rem' }}>↑ backing up… ~{fmtEta(remainSec)}</span>}
                              </div>
                              <div style={{ color: '#9ca3af', fontSize: '0.65rem', marginTop: '0.1rem' }}>HDD:{formatMb(res.hddMb)}</div>
                            </div>
                            {canRestore && <Btn label="Download" color="#60a5fa" onClick={() => handleRestoreSoftware(s)} disabled={busy} />}
                          </div>
                        );
                      })}
                      {hd.files.map(f => {
                        const remainSec = Math.ceil((new Date(f.readyAt).getTime() - nowTs) / 1000);
                        const ready     = remainSec <= 0;
                        const present   = player.files?.some(x => x.name === f.name);
                        const canRestore = ready && !present;
                        return (
                          <div key={`f-${f.name}`} style={rowS}>
                            <FileBadge type={f.type} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                                <span style={{ color: '#e2e8f0', fontSize: '0.8rem', overflowWrap: 'anywhere' }}>{f.name}</span>
                                {!ready && <span style={{ color: '#fbbf24', fontSize: '0.62rem' }}>↑ backing up… ~{fmtEta(remainSec)}</span>}
                              </div>
                              <div style={{ color: '#9ca3af', fontSize: '0.65rem', marginTop: '0.1rem' }}>{f.sizeMb.toFixed(2)} MB</div>
                            </div>
                            {canRestore && <Btn label="Download" color="#60a5fa" onClick={() => handleRestoreFile(f)} disabled={busy} />}
                          </div>
                        );
                      })}
                    </>
                  )}
                </>
              );
            })())}

          </div>
          <MsgBar msg={localMsg} />
        </div>

        {/* ──────────────────── REMOTE PANEL ─────────────────────────────────── */}
        {/* attackFx adds a one-shot green pulse / red shake after an attack resolves. */}
        <div
          style={{ ...panelS, ...(narrow && mobileTab !== 'remote' ? { display: 'none' } : null) }}
          className={attackFx ? `fx-${attackFx}` : undefined}
        >

          {session ? (
            /* ── Connected view ── */
            <>
              <div style={headerS}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{ color: '#4ade80', fontSize: '0.8rem', fontWeight: 'bold' }}>{session.targetIp}</span>
                  <span style={{ color: '#cbd5e1', fontSize: '0.72rem' }}>{session.targetUsername}</span>
                  <AccessBadge level={session.accessLevel} />
                  <span style={{ color: '#9ca3af', fontSize: '0.68rem' }}>{formatMbit(session.targetInternetMbit)}</span>
                </div>
                <div style={{ display: 'flex', gap: '0.35rem' }}>
                  {session.accessLevel === NodeAccessLevel.Full && (
                    <Btn
                      label="Infect"
                      color="#a78bfa"
                      onClick={handleInfect}
                      disabled={busy || !hasTrojan}
                    />
                  )}
                  <Btn label="Refresh"    color="#60a5fa" onClick={handleRefreshFiles} disabled={busy} hint={tut.refresh} />
                  <Btn label="Disconnect" color="#f87171" onClick={handleDisconnect} hint={tut.disconnect} />
                </div>
              </div>

              {/* Target hardware load (current usage vs total). Usage is derived
                  from the visible software + files + hosted infections so it tracks
                  Refresh; HDD also counts file occupancy (text/log/package files)
                  and the Trojan footprint, not just software. When software is
                  cloaked, only file + infection occupancy is shown. */}
              {(() => {
                const usedCpu = session.software.filter(s => s.isRunning).reduce((a, s) => a + getSoftwareResources(s.type, s.version).cpuMhz, 0)
                  + infectionsCpuMhz(session.infections);
                const usedRam = session.software.filter(s => s.isRunning).reduce((a, s) => a + getSoftwareResources(s.type, s.version).ramMb, 0)
                  + infectionsRamMb(session.infections);
                const usedHdd = session.software.filter(s => s.isDownloaded).reduce((a, s) => a + getSoftwareResources(s.type, s.version).hddMb, 0)
                  + filesHddMb(session.files) + infectionsHddMb(session.infections);
                return (
              <div style={{ display: 'flex', gap: '0.6rem', padding: '0.4rem 0.75rem', borderBottom: '1px solid #111', flexWrap: 'wrap' }}>
                {([
                  ['CPU', usedCpu, session.cpuMhz, formatMhz],
                  ['RAM', usedRam, session.ramMb, formatMb],
                  ['HDD', usedHdd, session.hddMb, formatMb],
                ] as [string, number, number, (n: number) => string][]).map(([label, used, total, fmt]) => {
                  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
                  const color = pct > 90 ? '#f87171' : pct > 70 ? '#fbbf24' : '#4ade80';
                  return (
                    <div key={label} style={{ flex: 1, minWidth: '92px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', fontFamily: 'monospace', marginBottom: '0.15rem' }}>
                        <span style={{ color: '#9ca3af' }}>{label}</span>
                        <span style={{ color }}>{fmt(used)} / {fmt(total)}</span>
                      </div>
                      <div style={{ height: '3px', background: '#1a1a2e', borderRadius: '2px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
                );
              })()}

              <div style={{ flex: 1, overflow: 'auto' }}>
                {/* Files */}
                <div style={sectionHdrS}>Files</div>
                {session.files.length === 0 ? (
                  <div style={emptyS}>No files on this system.</div>
                ) : session.files.map(f => {
                  const canRead     = session.accessLevel !== NodeAccessLevel.FtpOnly && f.type !== FileType.SoftwarePackage;
                  const canDownload = session.accessLevel !== NodeAccessLevel.SshOnly;
                  const canDelete   = session.accessLevel !== NodeAccessLevel.FtpOnly && f.isDeletable;
                  // A software package replaces the local copy of that type, so the server
                  // refuses it while the local copy runs / is still downloading. Surface the
                  // reason up front instead of letting the player discover it after clicking.
                  const pkgMine = f.type === FileType.SoftwarePackage && f.softwarePackageType
                    ? sw.find(m => m.type === f.softwarePackageType)
                    : undefined;
                  const pkgBlock = !pkgMine ? undefined
                    : pkgMine.version >= (f.softwarePackageVersion ?? 0)
                      ? `You already have ${pkgMine.type} v${pkgMine.version.toFixed(1)} or higher`
                      : pkgMine.isRunning
                        ? `Stop your running ${pkgMine.type} v${pkgMine.version.toFixed(1)} first`
                        : !pkgMine.isDownloaded
                          ? 'A download for this software is already in progress'
                          : undefined;
                  return (
                    <div key={f.id} style={rowS}>
                      <FileBadge type={f.type} />
                      <span style={{ flex: 1, color: '#e2e8f0', fontSize: '0.8rem', minWidth: 0, overflowWrap: 'anywhere' }}>
                        {f.name}{!f.isDeletable && <span style={{ color: '#666', marginLeft: '0.3rem' }}>🔒</span>}
                      </span>
                      <span style={{ color: '#9ca3af', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{f.sizeMb.toFixed(2)} MB</span>
                      {canRead     && <Btn label="Read"     onClick={() => handleRemoteRead(f)}     disabled={busy} />}
                      {canDownload && <Btn label="Download" color="#60a5fa" onClick={() => handleRemoteDownload(f)} disabled={busy || !!pkgBlock} title={pkgBlock} hint={tut.downloadList && f.name === SERVER_LIST_FILE} />}
                      {canDelete   && <Btn label="Delete"   color="#f87171" onClick={() => handleRemoteDelete(f)}  disabled={busy} hint={tut.deleteLog && f.name === ACCESS_LOG_FILE} />}
                    </div>
                  );
                })}

                {/* Installed software — visibility follows the Seeker vs Hider rule.
                    Stop/Delete need full root access (enforced by the backend too). */}
                <div style={sectionHdrS}>Installed software</div>
                {session.softwareHidden ? (
                  <div style={{ ...emptyS, color: '#fbbf24' }}>Software cloaked — a higher Seeker version is required</div>
                ) : session.software.length === 0 ? (
                  <div style={emptyS}>No software detected.</div>
                ) : session.software.map(s => {
                  const canControl = session.accessLevel === NodeAccessLevel.Full;
                  const canTransfer = session.accessLevel !== NodeAccessLevel.SshOnly;
                  // Eligible to pull when I lack this software or own a lower version.
                  const mine = sw.find(m => m.type === s.type);
                  const canGrab = canTransfer && (!mine || mine.version < s.version);
                  // Pulling replaces the local copy, so the server refuses it while that
                  // copy is running / still downloading — explain it on the button.
                  const grabBlock = !mine ? undefined
                    : mine.isRunning
                      ? `Stop your running ${mine.type} v${mine.version.toFixed(1)} first`
                      : !mine.isDownloaded
                        ? 'A download for this software is already in progress'
                        : undefined;
                  return (
                    <div key={s.id} style={rowS}>
                      <RoleBadge role={SOFTWARE_ROLE[s.type]} />
                      <span style={{ flex: 1, color: '#e2e8f0', fontSize: '0.8rem', minWidth: 0, overflowWrap: 'anywhere' }}>
                        {s.type}
                        {s.isRunning && <span style={{ color: '#4ade80', fontSize: '0.62rem', marginLeft: '0.4rem' }}>● RUN</span>}
                      </span>
                      <span style={{ color: '#9ca3af', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>v{s.version.toFixed(1)}</span>
                      {canGrab                    && <Btn label="Download" color="#60a5fa" onClick={() => handleRemoteDownloadSoftware(s)} disabled={busy || !!grabBlock} title={grabBlock} />}
                      {canControl && s.isRunning  && <Btn label="Stop"   color="#fbbf24" onClick={() => handleRemoteStopSoftware(s)}   disabled={busy} />}
                      {canControl && !s.isRunning && <Btn label="Delete" color="#f87171" onClick={() => handleRemoteDeleteSoftware(s)} disabled={busy} />}
                    </div>
                  );
                })}

                {/* Infections running on the target — view-only, no actions. */}
                <div style={sectionHdrS}>Infections</div>
                {session.infections.length === 0 ? (
                  <div style={emptyS}>No active infections.</div>
                ) : session.infections.map((inf, idx) => {
                  const res = getSoftwareResources(SoftwareType.Trojan, inf.virusVersion);
                  const start = new Date(inf.infectedAt).getTime();
                  const end   = new Date(inf.activeAt).getTime();
                  const remainSec = Math.ceil((end - nowTs) / 1000);
                  const uploading = remainSec > 0;
                  const pct = uploading && end > start
                    ? Math.min(100, Math.max(0, ((nowTs - start) / (end - start)) * 100))
                    : 100;
                  return (
                    <div key={idx} style={rowS}>
                      <RoleBadge role="ATK" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                          <span style={{ color: '#e2e8f0', fontSize: '0.8rem' }}>Trojan</span>
                          <span style={{ color: '#9ca3af', fontSize: '0.68rem' }}>v{inf.virusVersion.toFixed(1)}</span>
                          {uploading
                            ? <span style={{ color: '#fbbf24', fontSize: '0.62rem' }}>↑ infecting… ~{fmtEta(remainSec)}</span>
                            : <span style={{ color: '#f87171', fontSize: '0.62rem' }}>● active</span>}
                        </div>
                        <div style={{ color: '#9ca3af', fontSize: '0.62rem', marginTop: '0.1rem' }}>
                          HDD:{formatMb(res.hddMb)} · RAM:{formatMb(res.ramMb)} · CPU:{formatMhz(res.cpuMhz)}
                        </div>
                        {uploading && (
                          <div style={{ marginTop: '0.25rem' }}>
                            <div style={{ height: '3px', background: '#1a1a2e', borderRadius: '2px', overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${pct}%`, background: '#fbbf24', transition: 'width 0.5s ease' }} />
                            </div>
                            <span style={{ color: '#555', fontSize: '0.6rem' }}>{pct.toFixed(0)}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ padding: '0.35rem 0.75rem', background: '#080808', borderTop: '1px solid #111', color: '#9ca3af', fontSize: '0.68rem', fontFamily: 'monospace' }}>
                Access is being logged. Delete access.log before disconnecting.
              </div>
            </>
          ) : (
            /* ── Not connected view ── */
            <>
              <div style={headerS}>
                <span style={{ color: '#555', fontSize: '0.78rem', fontWeight: 'bold' }}>Remote System</span>
                <span style={{ color: '#6b7280', fontSize: '0.7rem' }}>not connected</span>
              </div>

              <div style={{ flex: 1, overflow: 'auto', padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>

                {/* IP input */}
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    value={targetIpInput}
                    onChange={e => { setTargetIpInput(e.target.value); setScanResult(null); }}
                    onKeyDown={e => e.key === 'Enter' && handleConnect()}
                    placeholder={`Target IP  (hint: ${WHOIS_1_IP})`}
                    disabled={busy || ipChangePending}
                    className={tut.ipInput ? 'fx-hint' : undefined}
                    style={{
                      flex: 1, background: '#111', border: '1px solid #2a2a2a',
                      color: '#e2e8f0', padding: '0.4rem 0.6rem', borderRadius: '4px',
                      fontFamily: 'monospace', fontSize: '0.8rem', minWidth: '150px',
                    }}
                  />
                  <Btn label="Connect" color="#4ade80" onClick={handleConnect} disabled={busy || noIp || ipChangePending} hint={tut.connect} />
                  <Btn label="Scan" color="#60a5fa" onClick={handleScan}
                    disabled={busy || noIp || !hasPortScanner || ipChangePending}
                    title={!hasPortScanner ? 'PortScanner must be running' : undefined}
                  />
                </div>

                {/* Already-compromised indicator */}
                {knownNode && (
                  <div style={{ background: '#0a150a', border: '1px solid #1a3d1a', borderRadius: '4px', padding: '0.45rem 0.65rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <AccessBadge level={knownNode.accessLevel} />
                    <span style={{ color: '#4ade80', fontSize: '0.75rem' }}>Previously compromised — {knownNode.username || 'unknown'}</span>
                  </div>
                )}

                {/* Attack buttons */}
                <div>
                  <div style={{ color: '#9ca3af', fontSize: '0.67rem', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Attack</div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <Btn label="Brute Force" color="#f87171"
                      onClick={() => handleHack(HackAttackType.BruteForce)}
                      disabled={busy || !hasCracker || noIp || ipChangePending}
                      title={!hasCracker ? 'Cracker must be running' : undefined}
                      hint={tut.bruteForce}
                    />
                    <Btn label="Exploit" color="#fbbf24"
                      onClick={() => handleHack(HackAttackType.Exploit)}
                      disabled={busy || !canExploit || noIp || ipChangePending}
                      title={!canExploit ? 'FtpExploit or SshExploit must be running' : undefined}
                    />
                    <Btn label="DDoS" color="#a78bfa"
                      onClick={() => handleHack(HackAttackType.DDoS)}
                      disabled={busy || !hasDdos || noIp || ipChangePending}
                      title={!hasDdos ? 'Ddos must be running' : undefined}
                    />
                  </div>
                </div>

                {/* IP change warning */}
                {ipChangePending && (
                  <div style={{ color: '#fbbf24', fontSize: '0.75rem', background: '#1a1400', border: '1px solid #4d3a00', borderRadius: '4px', padding: '0.4rem 0.65rem' }}>
                    IP change in progress — all actions are blocked.
                  </div>
                )}

                {/* Scan result */}
                {scanResult && (
                  <div style={{ background: '#0d0d0d', border: '1px solid #1a1a2e', borderRadius: '4px', padding: '0.6rem 0.75rem' }}>
                    <div style={{ color: 'var(--primary-color)', fontSize: '0.72rem', fontWeight: 'bold', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Scan — {targetIpInput}
                    </div>
                    {([
                      ['Username', scanResult.username],
                      ['Level',    String(scanResult.level)],
                      ['CPU',      formatMhz(scanResult.cpuMhz)],
                      ['HDD',      formatMb(scanResult.hddMb)],
                      ['RAM',      formatMb(scanResult.ramMb)],
                      ['Line',     formatMbit(scanResult.internetSpeedMBit)],
                    ] as [string, string][]).map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', padding: '0.08rem 0' }}>
                        <span style={{ color: '#9ca3af' }}>{k}</span>
                        <span style={{ color: '#e2e8f0' }}>{v}</span>
                      </div>
                    ))}
                    {scanResult.visibleSoftware.length > 0 ? (
                      <div style={{ marginTop: '0.4rem', borderTop: '1px solid #1a1a2e', paddingTop: '0.35rem' }}>
                        {scanResult.visibleSoftware.map(s => (
                          <div key={s.type} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem' }}>
                            <span style={{ color: SOFTWARE_ROLE[s.type] === 'DEF' ? '#f87171' : '#888' }}>{s.type}</span>
                            <span style={{ color: '#9ca3af' }}>v{s.version.toFixed(1)}</span>
                          </div>
                        ))}
                      </div>
                    ) : scanResult.softwareHidden ? (
                      <div style={{ color: '#fbbf24', fontSize: '0.7rem', marginTop: '0.3rem' }}>
                        Software hidden — a higher Seeker version is required
                      </div>
                    ) : (
                      <div style={{ color: '#9ca3af', fontSize: '0.7rem', marginTop: '0.3rem' }}>
                        No software detected
                      </div>
                    )}
                  </div>
                )}

                {/* Hint */}
                <div style={{ color: '#9ca3af', fontSize: '0.68rem' }}>
                  Entry node: {WHOIS_1_IP} [WhozNet-1]
                </div>
              </div>
            </>
          )}

          <MsgBar msg={remoteMsg} />
        </div>
      </div>

      {/* ── Sidebar ── */}
      {/* grow:1 so that when the row wraps (narrow / mobile-landscape screens) the
          sidebar is alone on its line and expands to the full width instead of staying
          stuck at 300px; beside the main panel the main panel's much larger grow (below)
          keeps this at ~300px, so the desktop layout is unchanged. */}
      <div style={{ flex: '1 1 300px', minWidth: '260px' }}>
        <PlayerStats />
      </div>

      {/* ── File read modal ── */}
      {readModal && (
        <FileReadModal
          name={readModal.name}
          content={readModal.content}
          onClose={() => setReadModal(null)}
          // Tapping an IP inside a file (e.g. the looted node list) loads it into
          // the target field and closes the reader.
          onPickIp={ip => { setTargetIpInput(ip); setScanResult(null); setReadModal(null); setMobileTab('remote'); }}
        />
      )}
    </div>
  );
}
